/**
 * @class AccordionItem
 * @extends HTMLElement
 *
 * Represents a single accordion item.
 */

class AccordionItem extends HTMLElement {
  constructor() {
    super();
  }

  selectors = {
    toggle: "[data-accordion-toggle]",
    content: "[data-accordion-content]"
  };

  modifiers = {
    open: "accordion-item--open"
  };

  connectedCallback() {
    this.toggleElement = this.querySelector(this.selectors.toggle);
    this.contentElement = this.querySelector(this.selectors.content);

    if (!this.toggleElement || !this.contentElement) {
      return;
    }

    this.toggleElement.addEventListener("click", () => {
      this.dispatchEvent(
        new CustomEvent("accordion:toggle", {
          bubbles: true,
          detail: {
            item: this
          }
        })
      );

      this.toggle();
    });

    if (this.isOpen) {
      this._updateState();
    }
  }

  get isOpen() {
    return this.hasAttribute("open");
  }

  set isOpen(value) {
    if (value) {
      this.setAttribute("open", "");
    } else {
      this.removeAttribute("open");
    }

    this._updateState();
  }

  open() {
    this.isOpen = true;
  }

  close() {
    this.isOpen = false;
  }

  toggle() {
    this.isOpen = !this.isOpen;
  }

  _updateState() {
    if (this.isOpen) {
      this.classList.add(this.modifiers.open);
      this.toggleElement.setAttribute("aria-expanded", "true");
      this.contentElement.style.maxHeight = `${this.contentElement.scrollHeight}px`;
    } else {
      this.classList.remove(this.modifiers.open);
      this.toggleElement.setAttribute("aria-expanded", "false");
      this.contentElement.style.maxHeight = "0";
    }
  }
}

if (!customElements.get("accordion-item")) {
  customElements.define("accordion-item", AccordionItem);
}

/**
 * Global click handler to close accordion items from the same group.
 */
document.addEventListener("DOMContentLoaded", () => {
  function AccordionController() {
    document.addEventListener("accordion:toggle", (event) => {
      const toggledItem = event.detail?.item;

      if (!toggledItem || !toggledItem.getAttribute("group")) {
        return;
      }

      const groupElements = document.querySelectorAll(`accordion-item[group="${toggledItem.getAttribute("group")}"]`);

      groupElements.forEach((item) => {
        if (!item.isSameNode(toggledItem) && typeof item.close === "function") {
          item.close();
        }
      });
    });
  }

  AccordionController();
});

/**
 * Global click handler for anchor elements.
 */
document.addEventListener ("DOMContentLoaded", () => {
  document.addEventListener("click", (event) => {
    const anchorElement = event.target.closest("a[href^='#']");

    if (!anchorElement) {
      return;
    }

    const targetUrl = new URL(anchorElement.href);

    if (targetUrl.hash === "") {
      return;
    }

    const targetElement = document.querySelector(targetUrl.hash);

    if (!targetElement) {
      return;
    }

    event.preventDefault();

    targetElement.scrollIntoView({
      behavior: "smooth"
    });
  });
});

/**
 * @class BoundedVisibility
 * @extends HTMLElement
 *
 * A custom element adding class when it is scrolled into view between two boundary elements.
 */
class BoundedVisibility extends HTMLElement {
  constructor() {
    super();
  }

  get _modifier() {
    return this.getAttribute("visible-class-name") || "is-visible";
  }

  get _fromElement() {
    return document.querySelector(this.getAttribute("from"));
  }

  get _toElement() {
    return document.querySelector(this.getAttribute("to"));
  }

  connectedCallback() {
    if (!this._fromElement || !this._toElement) {
      return;
    }

    this._observer = new IntersectionObserver(this._onIntersection.bind(this), {
      root: null,
      threshold: 0,
    });

    this._observer.observe(this._fromElement);
    this._observer.observe(this._toElement);
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
    }
  }

  _onIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.target === this._fromElement) {
        this._isFromElementScrolledPast = entry.boundingClientRect.bottom < 0;
      }

      if (entry.target === this._toElement) {
        this._isToElementScrolledPast = entry.boundingClientRect.bottom < 0;
      }
    });

    if (this._isFromElementScrolledPast && !this._isToElementScrolledPast) {
      this.classList.add(this._modifier);
    } else {
      this.classList.remove(this._modifier);
    }
  }
}

if (!customElements.get("bounded-visibility")) {
  customElements.define("bounded-visibility", BoundedVisibility);
}

/**
 * @class ConditionalVisibility
 * @extends HTMLElement
 *
 * A custom element adding class when an observed element is not in view.
 */
class ConditionalVisibility extends BoundedVisibility {
  constructor() {
    super();
  }

  get _observeElement() {
    return document.querySelector(this.getAttribute("observe"));
  }

  connectedCallback() {
    if (!this._observeElement) {
      return;
    }

    this._observer = new IntersectionObserver(this._onIntersection.bind(this), {
      root: null,
      threshold: 0,
    });

    this._observer.observe(this._observeElement);

    if (this._fromElement) {
      this._observer.observe(this._fromElement);
    }

    if (this._toElement) {
      this._observer.observe(this._toElement);
    }
  }

  _onIntersection(entries) {
    entries.forEach((entry) => {
      if (this._fromElement) {
        if (entry.target === this._fromElement) {
          this._isFromElementScrolledPast = entry.boundingClientRect.bottom < 0;
        }
      }

      if (entry.target === this._observeElement) {
        this._isObservableElementVisible = entry.isIntersecting;
      }

      if (this._toElement) {
        if (entry.target === this._toElement) {
          this._isToElementVisible = entry.isIntersecting;
        }
      }
    });

    const fromCondition = this._fromElement ? this._isFromElementScrolledPast : true;
    const toCondition = this._toElement ? !this._isToElementVisible : true;

    if (fromCondition && !this._isObservableElementVisible && toCondition) {
      this.classList.add(this._modifier);
    } else {
      this.classList.remove(this._modifier);
    }
  }
}

if (!customElements.get("conditional-visibility")) {
  customElements.define("conditional-visibility", ConditionalVisibility);
}

/**
 * @class ReadMore
 * @extends HTMLElement
 *
 * A custom read more toggleable element.
 */
class ViewMore extends HTMLElement {
  constructor() {
    super();

    this._onToggleListener = this._onToggle.bind(this);
    this._onKeyDownListener = this._onKeyDown.bind(this);
  }

  selectors = {
    toggle: "[data-view-more-toggle]",
    content: "[data-view-more-content]",
    contentPreview: "[data-view-more-content-preview]"
  };

  connectedCallback() {
    this._toggleElement = this.querySelector(this.selectors.toggle);
    this._contentElement = this.querySelector(this.selectors.content);
    this._contentPreviewElement = this.querySelector(this.selectors.contentPreview);

    if (!this._toggleElement || !this._contentElement || !this._contentPreviewElement) {
      return;
    }

    this._toggleElement.addEventListener("click", this._onToggleListener);
    this._toggleElement.addEventListener("keydown", this._onKeyDownListener);
  }

  disconnectedCallback() {
    this._toggleElement.removeEventListener("click", this._onToggleListener);
    this._toggleElement.removeEventListener("keydown", this._onKeyDownListener);
  }

  _onToggle() {
    const isExpanded = this._contentElement.hidden === false;

    this._contentElement.hidden = isExpanded;
    this._contentPreviewElement.classList.toggle("hidden", !isExpanded);
    this._toggleElement.textContent = isExpanded ? this.getAttribute("view-more-text") : this.getAttribute("view-less-text");
    this._toggleElement.setAttribute("aria-expanded", (!isExpanded).toString());
  }

  _onKeyDown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this._onToggle();
    }
  }
}

if (!customElements.get("view-more")) {
  customElements.define("view-more", ViewMore);
}

/**
 * @class CustomTabs
 * @extends HTMLElement
 */
class CustomTabs extends HTMLElement {
  constructor() {
    super();
    this.container = this;
  }

  get selectors() {
    return {
      control: "[data-tab-control]",
      content: "[data-tab-content]",
    };
  }

  get attributes() {
    return {
      index: "data-index"
    };
  }

  get modifiers() {
    return {
      active: "active"
    };
  }

  connectedCallback() {
    this.initTabs();
  }

  initTabs() {
    this.addEventListener("click", (event) => {
      const control = event.target.closest(this.selectors.control);
      if (!control || !this.contains(control)) return;

      if (control.classList.contains(this.modifiers.active)) return;

      const index = control.getAttribute(this.attributes.index);
      const content = this.querySelector(
        `${this.selectors.content}[${this.attributes.index}="${index}"]`
      );

      this.querySelectorAll(this.selectors.control).forEach((el) =>
        el.classList.remove(this.modifiers.active)
      );
      this.querySelectorAll(this.selectors.content).forEach((el) =>
        el.classList.remove(this.modifiers.active)
      );

      control.classList.add(this.modifiers.active);
      if (content) content.classList.add(this.modifiers.active);
    });

    this.addEventListener("keydown", (event) => {
      const control = event.target.closest(this.selectors.control);
      if (!control || !this.contains(control)) return;

      if (event.key === "Enter" || event.key === " ") {
        control.click();
        event.preventDefault();
      }
    });
  }
}

if (!customElements.get("custom-tabs")) {
  customElements.define("custom-tabs", CustomTabs);
}

/**
 * @class Quick Add Form
 * @extends HTMLElement
 */

if (!customElements.get("od-quick-add-form")) {
  class QuickAddForm extends HTMLFormElement {
    selectors = {
      cartDrawer: "cart-drawer"
    };

    constructor() {
      super();

      this._onFormSubmitted = this._onFormSubmit.bind(this);
    }

    connectedCallback() {
      this.cartDrawerElement = document.querySelector(this.selectors.cartDrawer);

      this.addEventListener("submit", this._onFormSubmitted);
    }

    disconnectedCallback() {
      this.removeEventListener("submit", this._onFormSubmitted);
    }

    async _onFormSubmit(event) {
      event.preventDefault();

      const formData = new FormData(this);

      if (this.cartDrawerElement) {
        formData.append(
          "sections",
          this.cartDrawerElement.getSectionsToRender().map((section) => section.id)
        );

        formData.append("sections_url", window.location.pathname);
        this.cartDrawerElement.setActiveElement(document.activeElement);
      }

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      const responseJson = await response.json();

      if (response.ok) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "product-form",
          productVariantId: formData.get("id")
        });

        this._onFormSubmitSuccess(responseJson);
      } else {
        publish(PUB_SUB_EVENTS.cartError, {
          source: "product-form",
          productVariantId: formData.get("id"),
          errors: response.description,
          message: response.message
        });
      }
    }

    _onFormSubmitSuccess(response) {
      if (this.cartDrawerElement) {
        this.cartDrawerElement.renderContents(response);
      }
    }
  }

  customElements.define("od-quick-add-form", QuickAddForm, { extends: "form" });
}

/**
 * @class Quick Add Form
 * @extends HTMLElement
 */

if (!customElements.get("od-quick-add-form")) {
  class QuickAddForm extends HTMLFormElement {
    selectors = {
      cartDrawer: "cart-drawer"
    };

    constructor() {
      super();

      this._onFormSubmitted = this._onFormSubmit.bind(this);
    }

    connectedCallback() {
      this.cartDrawerElement = document.querySelector(this.selectors.cartDrawer);

      this.addEventListener("submit", this._onFormSubmitted);
    }

    disconnectedCallback() {
      this.removeEventListener("submit", this._onFormSubmitted);
    }

    async _onFormSubmit(event) {
      event.preventDefault();

      const formData = new FormData(this);

      if (this.cartDrawerElement) {
        formData.append(
          "sections",
          this.cartDrawerElement.getSectionsToRender().map((section) => section.id)
        );

        formData.append("sections_url", window.location.pathname);
        this.cartDrawerElement.setActiveElement(document.activeElement);
      }

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      const responseJson = await response.json();

      if (response.ok) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "product-form",
          productVariantId: formData.get("id")
        });

        this._onFormSubmitSuccess(responseJson);
      } else {
        publish(PUB_SUB_EVENTS.cartError, {
          source: "product-form",
          productVariantId: formData.get("id"),
          errors: response.description,
          message: response.message
        });
      }
    }

    _onFormSubmitSuccess(response) {
      if (this.cartDrawerElement) {
        this.cartDrawerElement.renderContents(response);
      }
    }
  }

  customElements.define("od-quick-add-form", QuickAddForm, { extends: "form" });
}
